/**
 * Per-channel now-playing snapshot builder, extracted verbatim from
 * use-playback so the reporting shape is unit-testable without a DOM. The
 * hook passes its live media elements directly (they satisfy the minimal
 * structural interface below); tests pass plain objects.
 */

import type {
  DeviceMediaItem,
  DeviceSync,
  SlotSignageContent,
} from "@workspace/api-client-react";
import {
  busForChannel,
  isSlotActive,
  type ChannelNowPlaying,
  type QueueEntry,
} from "@workspace/player-core";
import { signageNowPlayingLabel } from "./signage-web.ts";

/** The slice of an HTMLMediaElement the snapshot builder reads. */
export interface MediaElementLike {
  currentTime: number;
  duration: number;
  paused: boolean;
}

/** Reported duration: the element's when finite/positive, else the item's. */
export function mediaDurationSec(
  el: MediaElementLike | null,
  fallback: number | null | undefined,
): number | null {
  return el && Number.isFinite(el.duration) && el.duration > 0
    ? Math.floor(el.duration)
    : (fallback ?? null);
}

/** Live playback state the snapshot builder describes (refs in the hook). */
export interface ChannelSnapshotSources {
  sync: DeviceSync | null;
  /** Entry on the shared audio element (null when the audio bus is idle). */
  current: QueueEntry | null;
  /** The shared audio element (or its active crossfade leg). */
  audio: MediaElementLike | null;
  /** Device global volume (0-100) and mute for the audio bus. */
  volume: number;
  muted: boolean;
  /** Signage bed item on the screen bus. */
  videoItem: DeviceMediaItem | null;
  /** Foreground/takeover item on the screen bus. */
  foregroundVideoItem: DeviceMediaItem | null;
  /** Hydrated web-page / widget signage payload on the screen bus. */
  signageContent: SlotSignageContent | null;
  /** The active screen element (foreground when present, else signage bed). */
  screenEl: MediaElementLike | null;
  /** Screen-bus volume (0-100) and mute (operator video sound). */
  screenVolume: number;
  screenMuted: boolean;
  /**
   * The screen bus is paused-and-blanked (CB-143). Signage with no <video>
   * element — a widget, a web page, a still — has nothing whose `.paused` we
   * could read, so WITHOUT this the row below reports "playing" while the
   * screen is black. A control UI derives its transport toggle from that state
   * (mobile ConsoleScreen: `playing = live?.state === "playing"`), so it keeps
   * offering PAUSE and can never send PLAY — the screen stays black and the
   * operator has no way back.
   */
  screenPaused: boolean;
}

/**
 * Build the per-channel transport snapshot for every enabled channel: the
 * active audio channel reports live position/duration off the shared audio
 * element; idle-but-enabled audio channels report a stopped state; the video
 * channel reports off the active (foreground or signage) video element. The
 * device-level summary stays derived from the active audio entry (in the
 * hook's report()).
 */
export function buildChannelSnapshots(
  sources: ChannelSnapshotSources,
  deviceState: "playing" | "paused" | "stopped",
): ChannelNowPlaying[] {
  const s = sources.sync;
  if (!s) return [];
  const { audio, current } = sources;
  const activeAudioChannel =
    current && busForChannel(current.channelType) === "audio"
      ? current.channelType
      : null;
  const audioVolume = sources.muted ? 0 : sources.volume;

  const out: ChannelNowPlaying[] = [];

  // Audio bus: music / ads / announcements share one element and are mutually
  // exclusive. The active channel reports live; other scheduled audio
  // channels report a "stopped" placeholder so controls can address them.
  for (const ch of s.plan.channels) {
    const ct = ch.channelType;
    if (busForChannel(ct) !== "audio") continue;
    const slot = ch.now ?? null;
    // Report a channel when its schedule slot is active OR when it is the
    // channel actually producing audio right now (a manual play-now, or a
    // track still finishing after its slot turned off). The console must
    // mirror REAL playback, not just the schedule — otherwise a playing
    // channel shows "Live idle" because its slot happens to be off.
    const isLiveHere = activeAudioChannel === ct && Boolean(current);
    if (!isSlotActive(slot) && !isLiveHere) continue;

    if (activeAudioChannel === ct && current) {
      out.push({
        channelType: ct,
        bus: "audio",
        state: deviceState,
        title: current.item.title ?? null,
        artist: current.item.artist ?? null,
        itemId: current.item.id ?? null,
        playlistId: current.playlistId,
        playlistName: current.playlistName,
        positionSec: Math.floor(audio?.currentTime ?? 0),
        durationSec: mediaDurationSec(audio, current.item.durationSec),
        volume: audioVolume,
        muted: sources.muted,
      });
    } else {
      out.push({
        channelType: ct,
        bus: "audio",
        state: "stopped",
        title: null,
        artist: null,
        itemId: null,
        playlistId: slot?.playlistId ?? null,
        playlistName: slot?.playlistName ?? null,
        positionSec: 0,
        durationSec: null,
        volume: audioVolume,
        muted: sources.muted,
      });
    }
  }

  // Screen bus: a single video channel that runs independently of audio.
  // During full-AV the video channel's sound plays on the shared audio
  // element, so its now-playing is sourced from that element; otherwise it
  // reflects the signage / foreground video surface.
  const videoCh = s.plan.channels.find(
    (c) => busForChannel(c.channelType) === "screen",
  );
  if (videoCh) {
    const videoSlot = videoCh.now ?? null;
    const avAudio = current?.channelType === "video";
    const activeVideo = sources.foregroundVideoItem ?? sources.videoItem;
    const signage = sources.signageContent;
    const el = sources.screenEl;
    const screenVolume = sources.screenMuted ? 0 : sources.screenVolume;
    if (isSlotActive(videoSlot) || avAudio || activeVideo || signage) {
      if (avAudio && current) {
        out.push({
          channelType: videoCh.channelType,
          bus: "screen",
          state: deviceState,
          title: current.item.title ?? activeVideo?.title ?? null,
          artist: current.item.artist ?? activeVideo?.artist ?? null,
          itemId: current.item.id ?? activeVideo?.id ?? null,
          playlistId: current.playlistId ?? videoSlot?.playlistId ?? null,
          playlistName:
            current.playlistName ?? videoSlot?.playlistName ?? null,
          positionSec: Math.floor(audio?.currentTime ?? 0),
          durationSec: mediaDurationSec(audio, current.item.durationSec),
          volume: audioVolume,
          muted: sources.muted,
        });
      } else if (activeVideo) {
        // A video or still image is on the screen. A displayed still has no
        // "playing" media element, so treat an image on screen as on air
        // rather than reporting the paused element (CB-078).
        const isImage = activeVideo.type === "image";
        // A blanked screen is paused whatever is on it — a still has no element
        // to interrogate, so the explicit flag is the only truth available.
        const videoPlaying = sources.screenPaused
          ? false
          : isImage
            ? true
            : Boolean(el && !el.paused);
        out.push({
          channelType: videoCh.channelType,
          bus: "screen",
          state: videoPlaying ? "playing" : "paused",
          title: activeVideo.title ?? null,
          artist: activeVideo.artist ?? null,
          itemId: activeVideo.id ?? null,
          playlistId: videoSlot?.playlistId ?? null,
          playlistName: videoSlot?.playlistName ?? null,
          positionSec: Math.floor(el?.currentTime ?? 0),
          durationSec: mediaDurationSec(el, activeVideo.durationSec),
          volume: screenVolume,
          muted: sources.screenMuted,
        });
      } else if (signage) {
        // Web-page / widget signage has no media element but is on air
        // whenever hydrated onto the slot — surface it so the control panel
        // and realtime now-playing reflect what the screen shows
        // (CB-023/027). Its id doubles as the proof-of-display itemId.
        out.push({
          channelType: videoCh.channelType,
          bus: "screen",
          state: sources.screenPaused ? "paused" : "playing",
          title: signage.title,
          artist: signageNowPlayingLabel(signage),
          itemId: signage.id,
          playlistId: videoSlot?.playlistId ?? null,
          playlistName: videoSlot?.playlistName ?? null,
          positionSec: 0,
          durationSec: null,
          volume: screenVolume,
          muted: sources.screenMuted,
        });
      } else {
        // Active slot but nothing hydrated yet → stopped placeholder.
        out.push({
          channelType: videoCh.channelType,
          bus: "screen",
          state: "stopped",
          title: null,
          artist: null,
          itemId: null,
          playlistId: videoSlot?.playlistId ?? null,
          playlistName: videoSlot?.playlistName ?? null,
          positionSec: 0,
          durationSec: null,
          volume: screenVolume,
          muted: sources.screenMuted,
        });
      }
    }
  }

  return out;
}
